import { cn } from '@/utils/cn';
import { usePlaceholderImage } from '@/hooks/usePlaceholderImage';

interface BlurFillImageProps {
  src: string;
  alt: string;
  /** Sizing/aspect classes for the frame, e.g. "aspect-[9/16]" */
  className?: string;
  /** width/height ratio of the frame (inline style) when no aspect class is given */
  ratio?: number;
  /** Classes that inset the foreground so overlays (controls, caption) stay off the artwork */
  foregroundInset?: string;
}

/**
 * Shows the whole image (contain) on top of a blurred, enlarged copy of itself,
 * so a landscape/square visual in a tall frame stays readable instead of being
 * hard-cropped, with no empty bars.
 */
export const BlurFillImage = ({ src, alt, className, ratio, foregroundInset }: BlurFillImageProps) => {
  const placeholder = usePlaceholderImage();
  const onError = (e: React.SyntheticEvent<HTMLImageElement>): void => {
    e.currentTarget.src = placeholder;
  };
  return (
    <div
      className={cn('relative overflow-hidden bg-gray-900', className)}
      style={ratio ? { aspectRatio: String(ratio) } : undefined}
    >
      <img
        src={src}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 h-full w-full scale-125 object-cover blur-2xl opacity-80"
        onError={onError}
      />
      <div className={cn('absolute', foregroundInset ?? 'inset-0')}>
        <img src={src} alt={alt} className="h-full w-full object-contain" onError={onError} />
      </div>
    </div>
  );
};
